import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Button } from './components/Button';
import { Card, CardContent } from './components/Card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/Accordion';
import { Badge } from './components/Badge';
import { Check, Star, Gift, ArrowRight, Sparkles, BookOpen, Clock, Target, Users, ShieldCheck, Download, Infinity as InfinityIcon, Instagram, Mail, Smartphone } from 'lucide-react';
import { TestimonialsMarquee } from './components/TestimonialsMarquee';
import { SocialProofNotifications } from './components/SocialProofNotifications';
import { Countdown } from './components/Countdown';
import { TomosSlider } from './components/TomosSlider';
export function App() {
  return <div className="min-h-screen w-full bg-background text-foreground selection:bg-primary/30 selection:text-primary-foreground antialiased" data-id="element-0" data-mp="28tmej1rry8wp">
      {/* Social proof notifications (fixed bottom-left, non-intrusive) */}
      <SocialProofNotifications data-id="element-1" data-mp="3ij65gd4jrr9g" />

      {/* 1. Sticky top nav */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md" data-id="element-2" data-mp="3ml12v6jcxkhy">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between" data-id="element-3" data-mp="3rbznujfaj6rj">
          <div className="font-heading font-bold text-base md:text-xl tracking-widest text-primary whitespace-nowrap" data-id="element-4" data-mp="6hq1ea16ddl">
            Sistema PRO COSMOS
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-muted-foreground" data-id="element-5" data-mp="2zng0ch1bierc">
            <a href="#tomos" className="hover:text-primary transition-colors" data-id="element-6" data-mp="38lro8basuyg3">
              Tomos
            </a>
            <a href="#bonus" className="hover:text-primary transition-colors" data-id="element-7" data-mp="3dkl0y1i9ilf7">
              Bonus
            </a>
            <a href="#faq" className="hover:text-primary transition-colors" data-id="element-8" data-mp="nwc0jp6vnpuo">
              FAQ
            </a>
          </div>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_0_15px_rgba(212,168,87,0.3)]" data-id="element-9" data-mp="st2gmciomlg3">
            <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-10" data-mp="3ubrhql7qei4i">
              Acceder Ahora
            </a>
          </Button>
        </div>
      </nav>

      {/* 2. Hero */}
      <section className="relative pt-3 pb-3 md:pt-8 md:pb-8 overflow-hidden" data-id="element-11" data-mp="3gkl9o0qdzc60">
        <div className="absolute inset-0 pointer-events-none" data-id="element-12" data-mp="3bmjqzgwvhev2">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-primary/[0.06] rounded-full blur-3xl" data-id="element-13" data-mp="3bo4lj6j6sxsx"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 max-w-4xl text-center" data-id="element-14" data-mp="3ljxuyh1m0b6h">
          <Badge variant="outline" className="mb-3 border-primary/50 text-primary uppercase tracking-widest px-4 py-1.5 text-xs font-semibold bg-primary/5 backdrop-blur-sm" data-id="element-15" data-mp="2xun4bsdz3x62">
            EL SISTEMA QUE ORGANIZA TU ASTROLOGÍA
          </Badge>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-[1.1] mb-5 text-foreground" data-id="element-16" data-mp="18byasb3eml36">
            Dejá de mirar cartas natales{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-17" data-mp="ygwodn0ijx4a">
              sin saber por dónde empezar.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-foreground/85 mb-7 leading-relaxed max-w-2xl mx-auto" data-id="element-18" data-mp="1r5al7zfv8ne9">
            A un <span className="text-primary font-semibold" data-id="element-19" data-mp="1w2104jfxpcy3">clic</span> de
            dejar atrás la confusión y comenzar a interpretar cartas natales con
            un método claro y estructurado.
          </p>

          {/* Static product mockup — visually aligned under subtitle */}
          <div className="relative flex justify-center -mt-[30%] -mb-[28%]" data-id="element-20" data-mp="6o5w1nlyaskb">
            <motion.img src="https://cdn.magicpatterns.com/uploads/tXvcGeFBjHSm9n4m72R439/Untitled_design_(2).png" alt="Sistema PRO COSMOS — colección completa de manuales y bonos de astrología" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="relative z-10 mx-auto w-[121%] max-w-none drop-shadow-[0_14px_16px_rgba(0,0,0,0.22)]" data-id="element-21" data-mp="apr8j7ukp4tz" />
          </div>

          {/* PRIMARY CTA — directly after mockup */}
          <div className="flex flex-col items-center justify-center gap-3 mb-4" data-id="element-22" data-mp="19v8aerlzrbf5">
            <Button asChild size="lg" className="relative w-full sm:w-auto text-lg md:text-xl px-10 py-8 h-auto font-extrabold tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_50px_rgba(212,168,87,0.6)] ring-2 ring-primary/40 hover:ring-primary/70 transition-all hover:scale-[1.04]" data-id="element-23" data-mp="14y820a1o5wkv">
              <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-24" data-mp="3gm7rrnvyqviy">
                QUIERO EMPEZAR AHORA <ArrowRight className="ml-2 h-5 w-5" data-id="element-25" data-mp="2058nb5rt06ao" />
              </a>
            </Button>
            <a href="#tomos" className="text-sm text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline" data-id="element-26" data-mp="254v39mz0o0n6">
              Ver contenido completo ↓
            </a>
          </div>
        </div>
      </section>

      {/* 2.1 Credibility & social proof metrics */}
      <section className="relative py-16 md:py-20 border-y border-border/40 bg-card/20 overflow-hidden" data-id="element-27" data-mp="2jsg8bmfgc9xq">
        <div className="absolute inset-0 pointer-events-none" data-id="element-28" data-mp="2nwe35kxquykz">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-29" data-mp="2stmp7wm0jmri"></div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-30" data-mp="ygoe5ashom34">
          <motion.div className="text-center mb-12 md:mb-14" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-31" data-mp="yir6w2l10tbj">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold leading-tight text-foreground max-w-3xl mx-auto" data-id="element-32" data-mp="2kpekxr6ljvm8">
              Respaldado por{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-33" data-mp="2tii7coz7zit0">
                +1.000 Astrólogos profesionales
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-34" data-mp="33g4prlfq9shq">
            {[{
            num: '+360',
            label: 'Páginas de Formación'
          }, {
            num: '9.8/10',
            label: 'Calificación',
            stars: true
          }, {
            num: '+50',
            label: 'Ejemplos y Casos Prácticos'
          }, {
            num: '100%',
            label: 'Probado Método Exacto'
          }].map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.55,
            ease: 'easeOut',
            delay: 0.08 * i
          }} className="group relative flex flex-col items-center text-center p-5 md:p-7 rounded-2xl bg-background/40 border border-border/40 backdrop-blur-sm hover:border-primary/40 hover:bg-background/60 transition-all" data-id="element-35" data-mp="2zclmj6a6zh8g" data-mpi={i}>
                <div className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-primary leading-none tracking-tight drop-shadow-[0_0_20px_rgba(212,168,87,0.35)] mb-3 md:mb-4" data-id="element-36" data-mp="2vu7nu1qkgtqb">
                  {stat.num}
                </div>
                <div className="text-[11px] md:text-sm text-foreground/85 font-medium uppercase tracking-wider leading-snug" data-id="element-37" data-mp="2hx168ahkx12o">
                  {stat.label}
                </div>
                {stat.stars && <div className="flex items-center gap-0.5 mt-2" aria-label="Calificación 5 de 5 estrellas" data-id="element-38" data-mp="287rg306apsh7">
                    {[0, 1, 2, 3, 4].map(s => <Star key={s} className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary fill-primary drop-shadow-[0_0_6px_rgba(212,168,87,0.5)]" data-id="element-39" data-mp="1y80nx557jpka" />)}
                  </div>}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 2.2 Emotional pain-points — "Si hoy te pasa esto…" */}
      <section className="relative py-24 overflow-hidden" data-id="element-40" data-mp="inrnfwd87wtj">
        <div className="absolute inset-0 pointer-events-none" data-id="element-41" data-mp="nncn6908097b">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-42" data-mp="3m6t9zpz0b6tm"></div>
        </div>

        <div className="container mx-auto px-4 max-w-2xl relative z-10" data-id="element-43" data-mp="3r3b846zu06wz">
          <div className="text-center mb-12" data-id="element-44" data-mp="3w2wbqupy53g1">
            <p className="text-xs uppercase tracking-[0.35em] text-primary/70 mb-4 font-medium" data-id="element-45" data-mp="4pjhx024jn6r">
              Si esto te resuena…
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground" data-id="element-46" data-mp="33f0d3kgth1gm">
              Si hoy te pasa{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-47" data-mp="38cjwszwaa6fy">
                esto…
              </span>
            </h2>
          </div>

          <ul className="space-y-5 mb-12" data-id="element-48" data-mp="2k8b1kvt43omm">
            {['Abrís una carta y no sabés qué mirar primero.', 'Saltás entre planetas y aspectos sin entender el panorama completo.', 'Sentís que estudiás mucho pero todo sigue desconectado.', 'Dudás de tus interpretaciones.', 'Te bloqueás frente a cartas reales.'].map((pain, i) => <li key={i} className="group flex items-start gap-4 p-5 rounded-xl bg-card/40 border border-border/40 backdrop-blur-sm hover:border-primary/40 transition-colors" data-id="element-49" data-mp="26etkrirt8q0a" data-mpi={i}>
                <div className="shrink-0 mt-1 flex items-center justify-center w-7 h-7 rounded-full border border-primary/30 bg-primary/5 text-primary/70 text-sm font-heading font-semibold" data-id="element-50" data-mp="1dqxp8hdz9453">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <p className="text-base md:text-lg text-foreground/90 leading-relaxed" data-id="element-51" data-mp="3vx617phpnrbv">
                  {pain}
                </p>
              </li>)}
          </ul>

          <div className="relative pt-10 text-center" data-id="element-52" data-mp="3mwzinp75o15u">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-primary/40" data-id="element-53" data-mp="3i0itcro7zgr6"></div>
            <p className="font-heading text-2xl md:text-3xl italic text-primary leading-tight mb-4" data-id="element-54" data-mp="3d0we6cotzqfq">
              La astrología no tiene por qué sentirse{' '}
              <span className="font-semibold" data-id="element-55" data-mp="3883a7r16sm4p">caótica</span>.
            </p>
            <p className="text-base md:text-lg text-foreground/85 leading-relaxed max-w-xl mx-auto" data-id="element-56" data-mp="348krn1wu8n51">
              COSMOS fue creado para ayudarte a interpretar con{' '}
              <span className="text-primary font-semibold" data-id="element-57" data-mp="2u93niox8ihb6">estructura</span>,{' '}
              <span className="text-primary font-semibold" data-id="element-58" data-mp="2pak1ypmjz7jc">claridad</span> y{' '}
              <span className="text-primary font-semibold" data-id="element-59" data-mp="2fnps6o0cbdwf">seguridad</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Opiniones de alumnas — moved before tomos */}
      <TestimonialsMarquee data-id="element-60" data-mp="26mrmm1a96ybq" />

      {/* 3. ¿Qué incluye la colección? — Tomos */}
      <section id="tomos" className="py-24 bg-card/30 border-y border-border/50 relative overflow-hidden" data-id="element-61" data-mp="26ounxhguwdvr">
        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-62" data-mp="2ye7k4qdcm5gq">
          <div className="text-center mb-14" data-id="element-63" data-mp="2yga6n0falggp">
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground mb-4" data-id="element-64" data-mp="33bz8xbip5bgp">
              ¿Qué incluye la{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-65" data-mp="2pjjcitkawbor">
                colección?
              </span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed" data-id="element-66" data-mp="3hb8qaein2bm9">
              Cinco tomos estructurados para llevarte desde el caos mental hasta
              interpretaciones claras y profesionales.
            </p>
          </div>

          <TomosSlider data-id="element-67" data-mp="15q85g8xkme9q" />
        </div>
      </section>

      {/* 4. Bonus */}
      <section id="bonus" className="py-24 relative overflow-hidden" data-id="element-68" data-mp="1ke2yxrjekmnv">
        <div className="absolute inset-0 pointer-events-none" data-id="element-69" data-mp="1kgfclsi4oav4">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-70" data-mp="1fgkp7yxka3et"></div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-71" data-mp="mr3zj91bt0ma">
          <div className="text-center mb-14" data-id="element-72" data-mp="msyn6v3zf6u2">
            <div className="flex justify-center mb-5" data-id="element-73" data-mp="11owcbtfhnaws">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/50 backdrop-blur-sm shadow-[0_0_20px_rgba(52,211,153,0.15)]" data-id="element-74" data-mp="worv216rac5i">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" data-id="element-75" data-mp="2d4utyloqf59m"></span>
                <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-emerald-300" data-id="element-76" data-mp="honywpthkseo">
                  GRATIS por el día de hoy
                </p>
              </div>
            </div>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground mb-4" data-id="element-77" data-mp="qm6swjp3yh2p">
              Además, llevás estos{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-78" data-mp="10m79vyzi8l3q">
                BONOS EXCLUSIVOS
              </span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed" data-id="element-79" data-mp="1abqxfhy5x34x">
              Incluidos sin costo adicional con tu compra de hoy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" data-id="element-80" data-mp="1jcfc8evdr7iv">
            {[{
            title: 'Biblioteca de Interpretaciones Reales',
            desc: 'Más de 100 interpretaciones listas para desbloquear claridad en cualquier lectura.'
          }, {
            title: 'Protocolo de Apertura de Carta Natal',
            desc: 'El sistema exacto para saber qué mirar primero en segundos.'
          }, {
            title: 'Plantillas Profesionales de Sesiones',
            desc: 'Estructuras listas para entregar sesiones claras, profundas y memorables.'
          }, {
            title: 'Framework de Interpretación Profesional',
            desc: 'Aprendé a interpretar con orden, coherencia y seguridad real.'
          }, {
            title: 'Mapa Mental Completo del Sistema',
            desc: 'Toda la metodología resumida visualmente para conectar información más rápido.'
          }, {
            title: 'Guión Profesional de Interpretación',
            desc: 'La estructura exacta para interpretar sin bloquearte frente a una carta real.'
          }, {
            title: 'Sistema de Sesiones Rentables',
            desc: 'Aprendé a estructurar servicios astrológicos más claros y profesionales.'
          }, {
            title: 'Banco de Preguntas para Sesiones Profundas',
            desc: 'Preguntas diseñadas para crear interpretaciones más humanas y memorables.'
          }, {
            title: 'Máquina de Contenido Infinito',
            desc: 'Un año entero de ideas de contenido para posicionarte como astróloga.'
          }, {
            title: 'Los 50 Casos Reales de Cartas Natales',
            desc: '50 cartas natales reales explicadas paso a paso por expertos.'
          }].map((bonus, i) => <div key={i} className="group flex items-start gap-5 p-6 md:p-7 bg-card/60 border border-border/50 rounded-2xl hover:border-primary/40 hover:bg-card/80 transition-all backdrop-blur-sm" data-id="element-81" data-mp="1uxsqck9devov" data-mpi={i}>
                <div className="shrink-0 flex items-center justify-center w-12 h-12 bg-primary/10 text-xl rounded-xl border border-primary/20 group-hover:bg-primary/15 transition-colors" data-id="element-82" data-mp="3fflflmvoejlt">
                  🎁
                </div>
                <div className="flex-1 min-w-0" data-id="element-83" data-mp="3pcgc608e29xg">
                  <h3 className="font-heading font-bold text-base md:text-lg leading-snug mb-2 text-foreground" data-id="element-84" data-mp="3pbogkpeskc76">
                    {bonus.title}
                    <span className="text-primary/70 text-xs font-medium tracking-wider ml-1.5 align-middle" data-id="element-85" data-mp="2wvbf2wy6lty">
                      COSMOS™
                    </span>
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-86" data-mp="byblf9juolxn">
                    {bonus.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. ¿Para quién es COSMOS? */}
      <section className="py-24 bg-card/30 border-y border-border/50 relative overflow-hidden" data-id="element-87" data-mp="1rwsgc3btz0eu">
        <div className="absolute inset-0 pointer-events-none" data-id="element-88" data-mp="1wvvm5pzmxanv">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-89" data-mp="1wuap9tpulfmu"></div>
        </div>

        <div className="container mx-auto px-4 max-w-4xl relative z-10" data-id="element-90" data-mp="26lyf5q6raom7">
          <div className="text-center mb-14" data-id="element-91" data-mp="18y6egm8hp59v">
            <p className="text-xs uppercase tracking-[0.35em] text-primary/70 mb-4 font-medium" data-id="element-92" data-mp="18zhimn5ukf1i">
              Hecho para vos
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground" data-id="element-93" data-mp="1iw41x7iiyy1f">
              ¿Para quién es{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-94" data-mp="1nw8jz1z300tr">
                COSMOS?
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-95" data-mp="2z9ku1ulfpfod">
            {['Para quienes abren cartas natales y no saben qué mirar primero.', 'Para quienes estudian astrología hace tiempo pero sienten todo desconectado.', 'Para quienes quieren interpretar con claridad y seguridad real.', 'Para quienes desean transformar información suelta en sesiones profesionales.', 'Para quienes sienten que la astrología puede ser más simple cuando existe un sistema.'].map((item, i) => <div key={i} className={'group flex items-start gap-4 p-6 bg-background/60 border border-border/50 rounded-2xl hover:border-primary/40 hover:bg-background/80 transition-all backdrop-blur-sm ' + (i === 4 ? 'md:col-span-2' : '')} data-id="element-96" data-mp="3w2tzplj8p4dx" data-mpi={i}>
                <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 border border-primary/30 group-hover:bg-primary/15 transition-colors" data-id="element-97" data-mp="1pb1jeagjvz2j">
                  <Check className="w-5 h-5 text-primary" strokeWidth={2.5} data-id="element-98" data-mp="2c7m13jbtpj7p" />
                </div>
                <p className="text-base md:text-lg text-foreground/90 leading-relaxed" data-id="element-99" data-mp="277zn37o67yxu">
                  {item}
                </p>
              </div>)}
          </div>

          <div className="relative pt-12 mt-12 text-center" data-id="element-100" data-mp="1xipwxxcw0qcd">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-primary/40" data-id="element-101" data-mp="1xgdezk74qfzb"></div>
            <p className="font-heading text-xl md:text-2xl italic text-primary leading-relaxed max-w-2xl mx-auto" data-id="element-102" data-mp="1tert59cmarcz">
              "COSMOS fue creado para transformar{' '}
              <span className="font-semibold" data-id="element-103" data-mp="1jjgf6t919o6a">caos mental</span> en{' '}
              <span className="font-semibold" data-id="element-104" data-mp="1ek3q01uow50j">interpretaciones claras</span>."
            </p>
          </div>
        </div>
      </section>

      {/* 8. Oferta especial */}
      <section className="py-24 relative overflow-hidden" data-id="element-105" data-mp="3dubusfimy3ik">
        <div className="absolute inset-0 pointer-events-none" data-id="element-106" data-mp="38ywivt46uhos">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" data-id="element-107" data-mp="3hf5efx3oefqi"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-108" data-mp="3le4hfc5uk2fb">
          <Card className="bg-card/80 backdrop-blur-xl border-primary/30 shadow-[0_0_40px_rgba(212,168,87,0.1)] overflow-hidden" data-id="element-109" data-mp="3q7h3bej55v4d">
            <div className="bg-gradient-to-b from-primary/10 to-transparent px-6 pt-12 pb-10 md:px-10 md:pt-14 md:pb-12 text-center border-b border-primary/15" data-id="element-110" data-mp="3v7uyhvzn9akz">
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mb-5 text-foreground" data-id="element-111" data-mp="32i4mbshkoiri">
                Tu sistema de interpretación{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-112" data-mp="3cfsqf912j2mc">
                  empieza hoy.
                </span>
              </h2>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-113" data-mp="rosa52ue6l5p">
                Pasá de sentirte perdida frente a una carta natal a interpretar
                con{' '}
                <span className="text-primary/90 font-medium" data-id="element-114" data-mp="3t88rnf4gw8zt">estructura</span>,{' '}
                <span className="text-primary/90 font-medium" data-id="element-115" data-mp="bzvluqfqi5al">claridad</span> y{' '}
                <span className="text-primary/90 font-medium" data-id="element-116" data-mp="7095w98iuay3">seguridad</span>.
              </p>

              <div className="flex justify-center mb-4" data-id="element-117" data-mp="3fg2dfykvkc30">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/8 border border-primary/30 text-primary/95 text-sm font-medium backdrop-blur-sm" role="status" data-id="element-118" data-mp="3fgkfxcy4rmjd">
                  <span data-id="element-119" data-mp="3kff8v7ptarh7">✨</span>
                  <span data-id="element-120" data-mp="3ke4537wd9jq3">
                    Quedan{' '}
                    <span className="font-heading font-bold text-primary mx-0.5" data-id="element-121" data-mp="2wpkxyvp1zczl">
                      12
                    </span>{' '}
                    accesos disponibles hoy
                  </span>
                </div>
              </div>

              <p className="text-xs md:text-sm text-muted-foreground/85 italic leading-relaxed max-w-xl mx-auto" data-id="element-122" data-mp="177nru1xxgsbw">
                Cada día más astrólogos están dejando de estudiar información
                suelta y empezando a interpretar con un sistema.
              </p>
            </div>

            <CardContent className="p-8 md:p-14 lg:p-16 bg-gradient-to-b from-card/40 to-card/60" data-id="element-123" data-mp="12ae1mk2pscn0">
              {/* PRICE HERO */}
              <div className="text-center mb-14" data-id="element-124" data-mp="1pwucvvm71w0v">
                <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-muted-foreground/80 mb-6 font-medium" data-id="element-125" data-mp="1uya11wjo4inh">
                  Todo el sistema completo por menos de lo que cuesta una sola
                  sesión.
                </p>

                <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-[10px] md:text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-[0.2em] mb-8 shadow-[0_0_20px_rgba(212,168,87,0.3)]" data-id="element-126" data-mp="1l1x7qzs1c6t9">
                  Hoy acceso completo
                </div>

                <p className="text-sm md:text-base text-muted-foreground mb-3" data-id="element-127" data-mp="pt4nrdy2h4pf">
                  Hoy pagás solo
                </p>

                <div className="relative inline-block mb-6" data-id="element-128" data-mp="g20copbii3zv">
                  <div className="absolute inset-0 -m-8 bg-primary/20 rounded-full blur-3xl pointer-events-none" data-id="element-129" data-mp="g2a1i9ktm53z"></div>
                  <div className="relative text-7xl md:text-8xl lg:text-9xl font-heading font-black text-primary leading-none tracking-tight drop-shadow-[0_0_30px_rgba(212,168,87,0.5)]" data-id="element-130" data-mp="b12sk76xyzvq">
                    $19.900
                  </div>
                </div>

                <div className="flex flex-col items-center gap-3" data-id="element-131" data-mp="yr6vsdg7wxm7">
                  <Badge variant="outline" className="border-emerald-500/60 text-emerald-300 bg-emerald-500/10 px-5 py-2 text-sm md:text-base font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(52,211,153,0.15)]" data-id="element-132" data-mp="urfx7nkg9d74">
                    Ahorrás $109.500
                  </Badge>
                  <p className="text-base md:text-lg font-semibold text-foreground/95" data-id="element-133" data-mp="1v0o3fyde6dbe">
                    Pago único · Acceso de por vida
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm text-primary/90 font-medium" data-id="element-134" data-mp="23z9g5cw6my49">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-135" data-mp="28qrh40y7gyp8"></span>
                    Acceso inmediato tras la compra
                  </div>
                </div>
              </div>

              {/* PRIMARY CTA */}
              <div className="text-center mb-10" data-id="element-136" data-mp="2iqifidpvzxul">
                <Button asChild size="lg" className="relative w-full text-lg md:text-2xl py-9 md:py-10 h-auto font-extrabold tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_60px_rgba(212,168,87,0.6)] ring-2 ring-primary/40 hover:ring-primary/70 transition-all hover:scale-[1.02]" data-id="element-137" data-mp="2mrc2n4vi98mf">
                  <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-138" data-mp="20lgpfxkodn8s">
                    Desbloquear COSMOS
                    <ArrowRight className="ml-2 h-5 w-5" data-id="element-139" data-mp="29o803v5q8ezd" />
                  </a>
                </Button>
              </div>

              {/* Trust badges — below CTA */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3 mb-10" data-id="element-140" data-mp="2jjtb2isrwpl9">
                {[{
                icon: '🔒',
                label: 'Pago seguro'
              }, {
                icon: '⚡',
                label: 'Acceso en 5 min'
              }, {
                icon: '🛡️',
                label: 'Garantía 7 días'
              }, {
                icon: '💳',
                label: 'Mercado Pago'
              }].map((badge, i) => <div key={i} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-background/40 border border-border/40 backdrop-blur-sm text-xs md:text-sm font-medium text-foreground/85" data-id="element-141" data-mp="3gyubd2y15vzo" data-mpi={i}>
                    <span className="text-base shrink-0" aria-hidden="true" data-id="element-142" data-mp="3by6jkf1z9b4e">
                      {badge.icon}
                    </span>
                    <span data-id="element-143" data-mp="2y5qb2yqb4kw3">{badge.label}</span>
                  </div>)}
              </div>

              {/* Countdown timer — right below trust badges */}
              <div className="mb-12" data-id="element-144" data-mp="2oaybxqh2tpz7">
                <Countdown data-id="element-145" data-mp="2o7sp6hvx706n" />
              </div>

              <div className="text-center mb-12 space-y-1.5" data-id="element-146" data-mp="2t9rsjrrntx3l">
                <p className="text-sm md:text-base text-foreground/90 font-medium" data-id="element-147" data-mp="2f979qvlqw1a3">
                  Hoy desbloqueás toda la colección COSMOS con un único acceso.
                </p>
                <p className="text-sm text-primary/90 font-semibold" data-id="element-148" data-mp="2jcdbpr1tr6ar">
                  Quedan pocos accesos disponibles.
                </p>
              </div>

              <div className="flex items-center gap-4 mb-10" data-id="element-149" data-mp="2us9umrx3afmn">
                <div className="flex-1 h-px bg-border/40" data-id="element-150" data-mp="2prcgcqf7a5qf"></div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60 font-medium" data-id="element-151" data-mp="2ptymqmqcuf72">
                  Desglose
                </span>
                <div className="flex-1 h-px bg-border/40" data-id="element-152" data-mp="2gs8trcgtr876"></div>
              </div>

              <div className="space-y-3 mb-10 text-sm" data-id="element-153" data-mp="2c07b98t2cl8p">
                {[{
                name: 'COSMOS I',
                price: '$19.900',
                isMain: true
              }, {
                name: 'COSMOS II',
                price: '$19.900'
              }, {
                name: 'COSMOS III',
                price: '$19.900'
              }, {
                name: 'COSMOS IV',
                price: '$19.900'
              }, {
                name: 'COSMOS V',
                price: '$19.900'
              }, {
                name: 'Bonus Exclusivos',
                price: '$29.900'
              }].map((item, i) => <div key={i} className="flex justify-between items-center py-1.5" data-id="element-154" data-mp="mgzap5udu56k" data-mpi={i}>
                    <span className={item.isMain ? 'text-foreground/85 font-semibold' : 'text-muted-foreground/60'} data-id="element-155" data-mp="3q13q3cx1prp7">
                      {item.name}
                      {item.isMain && <span className="ml-2 text-[10px] text-primary/70 font-medium uppercase tracking-wider" data-id="element-156" data-mp="328diffw6dukq">
                          (principal)
                        </span>}
                    </span>
                    <div className="flex items-center gap-2.5" data-id="element-157" data-mp="2lyhx9el9ispc">
                      {item.isMain ? <span className="text-primary/90 font-semibold text-sm" data-id="element-158" data-mp="27ziakchb8nvt">
                          {item.price}
                        </span> : <>
                          <span className="line-through text-muted-foreground/40 text-xs" data-id="element-159" data-mp="1y8w0syw9yrl1">
                            {item.price}
                          </span>
                          <span className="text-primary/70 font-medium text-[10px] uppercase tracking-wider" data-id="element-160" data-mp="231p7vtf7uc0a">
                            Bonificado
                          </span>
                        </>}
                    </div>
                  </div>)}

                <div className="flex justify-between items-center pt-4 mt-4 border-t border-border/40 text-sm" data-id="element-161" data-mp="1fcnt7ofsi4mk">
                  <span className="text-muted-foreground/70" data-id="element-162" data-mp="10nkxvbj1ab7k">
                    Valor real total:
                  </span>
                  <span className="line-through text-muted-foreground/50" data-id="element-163" data-mp="vpb6lc0fjur8">
                    $129.400
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 10. Preguntas frecuentes */}
      <section id="faq" className="py-24 relative overflow-hidden" data-id="element-164" data-mp="1jm74t718ewje">
        <div className="absolute inset-0 pointer-events-none" data-id="element-165" data-mp="1jkdt3dbrhm4j">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-166" data-mp="1emc9mrafbksj"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-167" data-mp="1odff05adylxm">
          <div className="text-center mb-12" data-id="element-168" data-mp="1ofjfed92t2t5">
            <p className="text-xs uppercase tracking-[0.35em] text-primary/70 mb-4 font-medium" data-id="element-169" data-mp="22dzwmy4kebpj">
              Lo que querés saber
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground" data-id="element-170" data-mp="1xbsh5ykj0dig">
              Preguntas{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-171" data-mp="zz0oahykcgdn">
                frecuentes
              </span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4" data-id="element-172" data-mp="18rlvr1wqv0n6">
            {[{
            q: '¿En qué formato recibo COSMOS?',
            a: 'Recibirás toda la colección en formato digital con acceso inmediato después de la compra. Podrás verla cuando quieras y avanzar a tu ritmo.'
          }, {
            q: '¿Puedo usarlo desde el celular?',
            a: 'Sí. Toda la colección está optimizada para celular, tablet y computadora para que puedas estudiar desde cualquier lugar.'
          }, {
            q: '¿Sirve si todavía me siento perdida interpretando?',
            a: 'Sí. COSMOS fue creado justamente para ayudarte a dejar de sentir caos frente a una carta natal y empezar a interpretar con estructura y claridad.'
          }, {
            q: '¿Los bonus están incluidos?',
            a: 'Sí. Al acceder hoy desbloqueás toda la colección completa junto con todos los bonus incluidos.'
          }, {
            q: '¿Cuándo obtengo acceso?',
            a: 'El acceso es inmediato. Apenas completes tu compra recibirás toda la colección automáticamente.'
          }, {
            q: '¿Tiene garantía?',
            a: 'Sí. Probá COSMOS durante 7 días. Si no cumple tus expectativas, te devolvemos el 100% de tu dinero.'
          }].map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="bg-card/60 backdrop-blur-sm border border-border/50 rounded-2xl px-6 md:px-7 data-[state=open]:border-primary/40 data-[state=open]:bg-card/80 transition-all" data-id="element-173" data-mp="3qzraavslsrc9" data-mpi={i}>
                <AccordionTrigger className="text-left font-heading font-semibold text-base md:text-lg hover:text-primary py-6 leading-snug" data-id="element-174" data-mp="78gx9iao0ckm">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/80 text-base pb-6 leading-relaxed" data-id="element-175" data-mp="3uceremp6z9dl">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <div className="relative pt-12 mt-12 text-center" data-id="element-176" data-mp="uzdzg3cnrvio">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-primary/40" data-id="element-177" data-mp="uyl3zyj5gsfu"></div>
            <p className="font-heading text-xl md:text-2xl italic text-primary leading-relaxed max-w-2xl mx-auto" data-id="element-178" data-mp="gzbnexr17wbc">
              "No necesitás memorizar{' '}
              <span className="font-semibold" data-id="element-179" data-mp="31pkk6rhdy6i2">más información</span>.
              <br className="hidden md:block" data-id="element-180" data-mp="36ox5v8wit7j2" />
              Necesitás un{' '}
              <span className="font-semibold" data-id="element-181" data-mp="36mu5buxqrw03">sistema para entenderla</span>."
            </p>
          </div>
        </div>
      </section>

      {/* 11. Final CTA section */}
      <section className="py-32 relative overflow-hidden bg-card border-t border-border/50 text-center" data-id="element-182" data-mp="2u5jetmbde827">
        <div className="absolute inset-0 pointer-events-none" data-id="element-183" data-mp="385kewzjt9bgm">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl" data-id="element-184" data-mp="336hha065u7io"></div>
        </div>
        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-185" data-mp="2ff2e656k7m6d">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-foreground" data-id="element-186" data-mp="2ajwuld0mqjlg">
            Tu próxima interpretación comienza hoy.
          </h2>
          <p className="text-xl text-muted-foreground mb-12" data-id="element-187" data-mp="2kfztma06q246">
            La diferencia entre sentirte perdida y sentir claridad comienza con
            un sistema.
          </p>
          <Button asChild size="lg" className="text-xl px-10 py-8 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(212,168,87,0.4)] transition-all hover:scale-105 w-full sm:w-auto" data-id="element-188" data-mp="1rru8hm57yb3t">
            <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-189" data-mp="ly3wke7cn1d">
              DESBLOQUEAR COSMOS
            </a>
          </Button>
        </div>
      </section>

      {/* 11.5 Librofy trust badge + Instagram + Email — moved to bottom */}
      <section className="py-16 relative border-t border-border/40" data-id="element-190" data-mp="3dtsr85mxmx12">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-191" data-mp="3dw5ij9ekyjqs">
          <div className="flex flex-col items-center text-center" data-id="element-192" data-mp="3hv4l4ncua4f2">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground/70 mb-6 font-medium" data-id="element-193" data-mp="3mvij1ckq7xzd">
              Una compra inteligente respaldada por
            </p>
            <a href="https://www.instagram.com/librofy.online" target="_blank" rel="noopener noreferrer" className="inline-block transition-all hover:-translate-y-1 hover:drop-shadow-[0_15px_30px_rgba(255,140,50,0.35)]" aria-label="Visitá nuestro Instagram" data-id="element-194" data-mp="x81bi3bk0m1f">
              <img src="https://cdn.magicpatterns.com/uploads/e1ZfmCqnnwuhZanYAJfgbJ/logo_final_para_la_web.png" alt="Librofy — Compra Inteligente" className="h-[22rem] md:h-[28rem] w-auto drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)]" data-id="element-195" data-mp="12hu1atedt0oj" />
            </a>

            <p className="mt-6 mb-4 text-sm md:text-base text-foreground/80 max-w-xl" data-id="element-196" data-mp="oickb07burpq">
              ✨ Consultanos sin compromiso. Respondemos tus mensajes en pocos
              minutos.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-sm" data-id="element-197" data-mp="fjr2565rpeph">
              <a href="https://www.instagram.com/librofy.online" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" data-id="element-198" data-mp="2dwr559f85ufy">
                <Instagram className="h-4 w-4" data-id="element-199" data-mp="2x5qxmy90z8n4" />
                <span data-id="element-200" data-mp="2x8myauc9cbf9">@librofy.online</span>
              </a>
              <span className="hidden sm:inline text-muted-foreground/30" data-id="element-201" data-mp="2jem84ozdfwqi">
                •
              </span>
              <a href="mailto:soportelibrofy@gmail.com" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" data-id="element-202" data-mp="2ocnp94d8llzc">
                <Mail className="h-4 w-4" data-id="element-203" data-mp="2ehlxe0rku5t2" />
                <span data-id="element-204" data-mp="20h2uf81yxpxr">soportelibrofy@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 12. Footer */}
      <footer className="py-8 bg-background border-t border-border/40 text-center text-muted-foreground text-sm" data-id="element-205" data-mp="25hqjvpajtypv">
        <div className="container mx-auto px-4" data-id="element-206" data-mp="3usq1pu21riv1">
          <p className="font-heading font-medium tracking-widest text-foreground/60 mb-2" data-id="element-207" data-mp="3n83insvkngk">
            COSMOS™
          </p>
          <p data-id="element-208" data-mp="dsg39uqladut">© 2026 Todos los derechos reservados</p>
        </div>
      </footer>
    </div>;
}